import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, escucharCambios } from '../api';

type Estado<T> = {
  datos: T | null;
  cargando: boolean;
  error: string | null;
  recargar: () => void;
  /** Reemplaza los datos en memoria con la respuesta de una mutación. */
  fijar: (datos: T) => void;
};

/**
 * Cada cuánto se refrescan solas las pantallas que muestran trabajo de otra
 * persona, **además** del aviso en el momento que manda el servidor (ver
 * `alCambiar`). Es la red de seguridad para lo que no pasa por un pedido —el
 * paso del tiempo, una quincena que vence— y para un aviso que se perdió.
 */
export const VIVO = { rapido: 30_000, normal: 60_000 } as const;

/**
 * Una sola conexión SSE por pestaña, compartida por todas las pantallas que
 * escuchan. Los avisos que llegan juntos (una operación que toca tres tablas
 * son tres escrituras) se juntan en uno: si no, cada pantalla pediría lo suyo
 * tres veces seguidas.
 */
const oyentes = new Set<() => void>();
let dejarDeEscuchar: (() => void) | null = null;
let juntando: ReturnType<typeof setTimeout> | undefined;

function avisar() {
  clearTimeout(juntando);
  juntando = setTimeout(() => oyentes.forEach((f) => f()), 300);
}

/** `f` corre cada vez que algo cambia en el servidor, lo haya hecho quien sea. */
export function alCambiar(f: () => void): () => void {
  oyentes.add(f);
  dejarDeEscuchar ??= escucharCambios(avisar);
  return () => {
    oyentes.delete(f);
    if (oyentes.size === 0) {
      dejarDeEscuchar?.();
      dejarDeEscuchar = null;
    }
  };
}

/**
 * Un contador que avanza cuando el servidor avisa que algo cambió, solo cada
 * `ms`, y también cuando se vuelve a la pestaña. Cero lo deja quieto.
 *
 * Con la pestaña escondida no avanza: nadie está mirando, y el depósito tiene
 * la pantalla abierta todo el día en una tablet. Al volver avanza enseguida,
 * sin esperar el próximo tic, que es cuando la pantalla está más vieja.
 */
export function useLatido(ms: number): number {
  const [latido, setLatido] = useState(0);

  useEffect(() => {
    if (!ms) return;
    const avanzar = () => !document.hidden && setLatido((n) => n + 1);
    const cada = setInterval(avanzar, ms);
    window.addEventListener('focus', avanzar);
    document.addEventListener('visibilitychange', avanzar);
    const dejar = alCambiar(avanzar);
    return () => {
      dejar();
      clearInterval(cada);
      window.removeEventListener('focus', avanzar);
      document.removeEventListener('visibilitychange', avanzar);
    };
  }, [ms]);

  return latido;
}

/**
 * Cuánto esperar antes de reintentar la carga inicial, o `null` para dejar de
 * insistir y mostrar el error. Sólo se reintenta una caída de red (el `status`
 * 0 con el que `pedir` marca que ni llegó a contactar al servidor): un 4xx/5xx
 * es una respuesta del servidor, insistir no la cambia. Wifi de depósito: un
 * corte de un segundo no tiene por qué convertirse en un cartel de error.
 */
export function esperaReintento(err: unknown, intentosPrevios: number): number | null {
  if (!(err instanceof ApiError) || err.status !== 0) return null;
  if (intentosPrevios >= 2) return null;
  return intentosPrevios === 0 ? 1_000 : 2_000;
}

/**
 * GET con estados de carga y error. `deps` funciona como en useEffect: cambiar
 * un filtro dispara una recarga y descarta la respuesta anterior si llega tarde.
 *
 * Con `refrescoMs` la pantalla se mantiene sola, en el momento: lo que otro
 * usuario —o el escáner de arriba, en esta misma pestaña— hace del otro lado
 * (un pedido que entra, un archivo que alguien subió, una posición que se
 * ocupó) aparece sin que nadie apriete F5. Nadie en el depósito va a
 * estar recargando la página para enterarse.
 *
 * Ese refresco es **callado**: no prende «Cargando…» ni borra lo que está en
 * pantalla, y si falla no muestra el error. Una lista que parpadea cada diez
 * segundos es peor que una desactualizada, y un corte de un segundo no tiene
 * por qué vaciar la pantalla de quien está trabajando.
 */
export function useApi<T>(cargar: () => Promise<T>, deps: unknown[], refrescoMs = 0): Estado<T> {
  const [datos, setDatos] = useState<T | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const cargarRef = useRef(cargar);
  cargarRef.current = cargar;

  useEffect(() => {
    let vigente = true;
    let temporizador: ReturnType<typeof setTimeout> | undefined;
    setCargando(true);

    const intentar = (intentosPrevios: number) => {
      cargarRef
        .current()
        .then((res) => {
          if (!vigente) return;
          setDatos(res);
          setError(null);
          setCargando(false);
        })
        .catch((err: unknown) => {
          if (!vigente) return;
          const espera = esperaReintento(err, intentosPrevios);
          if (espera !== null) {
            temporizador = setTimeout(() => intentar(intentosPrevios + 1), espera);
            return;
          }
          setError(err instanceof ApiError ? err.message : 'Ocurrió un error inesperado.');
          setCargando(false);
        });
    };
    intentar(0);

    return () => {
      vigente = false;
      clearTimeout(temporizador);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  const latido = useLatido(refrescoMs);

  useEffect(() => {
    if (latido === 0) return;
    let vigente = true;
    cargarRef
      .current()
      .then((res) => {
        if (!vigente) return;
        setDatos(res);
        setError(null);
      })
      .catch(() => {});
    return () => {
      vigente = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latido]);

  const recargar = useCallback(() => setTick((t) => t + 1), []);

  return { datos, cargando, error, recargar, fijar: setDatos };
}

/** Mensaje de error legible para cualquier excepción de la API. */
export const mensajeDeError = (err: unknown) =>
  err instanceof ApiError ? err.message : 'Ocurrió un error inesperado.';

/** `valor`, pero recién cuando dejó de cambiar `ms`: para no pedir con cada tecla. */
export function useDemorado<T>(valor: T, ms = 250): T {
  const [demorado, setDemorado] = useState(valor);
  useEffect(() => {
    const id = setTimeout(() => setDemorado(valor), ms);
    return () => clearTimeout(id);
  }, [valor, ms]);
  return demorado;
}

/** Mientras `activo`, Escape llama a `alCerrar`. */
export function useEscape(activo: boolean, alCerrar: () => void): void {
  const ref = useRef(alCerrar);
  ref.current = alCerrar;
  useEffect(() => {
    if (!activo) return;
    const escape = (e: KeyboardEvent) => e.key === 'Escape' && ref.current();
    document.addEventListener('keydown', escape);
    return () => document.removeEventListener('keydown', escape);
  }, [activo]);
}
